import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import styles from "../passwordModal/assignModal.module.css";
import { Close } from "../../../Svg";
import { updateProductInventory } from "../../../redux/productSlice";
import { useDispatch } from "react-redux";

const statusOptions = ["OUT OF STOCK", "PUBLISHED", "DRAFT"];

const UpdateInventoryModal = ({ isOpen, onClose, onAssign, datas }) => {

    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [focusedField, setFocusedField] = useState(null);

    const formik = useFormik({
        initialValues: {
            status: datas?.status || "PUBLISHED",
            qtyAvailable: datas?.inventory?.qtyAvailable ?? 0,
            breakQty: datas?.quantityTable?.[0]?.breakQty || 1000,
            unitPrice: datas?.quantityTable?.[0]?.unitPrice ?? "",
        },
        validationSchema: Yup.object({
            status: Yup.string().required("Status is required"),
            qtyAvailable: Yup.number()
                .typeError("Must be a number")
                .required("Stock is required"),
            breakQty: Yup.number()
                .typeError("Must be a number")
                .nullable(),
            unitPrice: Yup.number()
                .transform((value, originalValue) => originalValue === "" ? null : value)
                .typeError("Must be a number")
                .nullable(),
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            const payloadValues = {
                status: values.status,
                qtyAvailable: (values.qtyAvailable !== "" && values.qtyAvailable !== null && values.qtyAvailable !== undefined) ? Number(values.qtyAvailable) : 0,
                breakQty: (values.breakQty !== "" && values.breakQty !== null && values.breakQty !== undefined) ? Number(values.breakQty) : 1000,
                ...(values.unitPrice !== "" && values.unitPrice !== null && values.unitPrice !== undefined
                    ? { unitPrice: Number(values.unitPrice) }
                    : {}),
            };
            const data = {
                id: datas._id,
                values: payloadValues
            };
            const status = await dispatch(updateProductInventory(data));
            if (status.payload == 200 || status.type?.endsWith('/fulfilled')) {
                onClose();
            }
            setLoading(false);
        },
    });

    if (!isOpen) return null;

    const prodNum = datas?.productOverview?.productNumber?.[0] || datas?.productOverview?.productNumber;

    const getInputStyle = (fieldName) => ({
        width: '100%',
        height: '42px',
        padding: '0 14px',
        borderRadius: '8px',
        border: focusedField === fieldName ? '1.5px solid #512DA8' : '1px solid #D0D5DD',
        boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(81, 45, 168, 0.12)' : 'none',
        fontSize: '14px',
        color: '#101828',
        background: focusedField === fieldName ? '#FFFFFF' : '#FAFAFC',
        fontFamily: `'Public Sans', sans-serif`,
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'all 0.15s ease-in-out'
    });

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
        }}>
            <div style={{
                background: '#FFFFFF',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '520px',
                padding: '28px 32px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.18)',
                boxSizing: 'border-box'
            }}>
                {/* Modal Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ margin: 0, fontSize: '19px', fontWeight: '700', color: '#101828', fontFamily: `'Public Sans', sans-serif` }}>Update Inventory</h2>
                            {prodNum && (
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    background: '#F0EEFF',
                                    color: '#512DA8',
                                    fontFamily: `'Public Sans', sans-serif`
                                }}>
                                    {prodNum}
                                </span>
                            )}
                        </div>
                        <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#667085', fontFamily: `'Public Sans', sans-serif` }}>
                            Update status, stock quantity, and pricing details
                        </p>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        style={{
                            background: '#F2F4F7',
                            border: 'none',
                            cursor: 'pointer',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#667085',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <Close />
                    </button>
                </div>

                <form onSubmit={formik.handleSubmit}>
                    {/* Status Dropdown */}
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                            Status
                        </label>
                        <select
                            name="status"
                            onChange={formik.handleChange}
                            onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                            onFocus={() => setFocusedField('status')}
                            value={formik.values.status}
                            style={getInputStyle('status')}
                        >
                            <option value="">Select Status...</option>
                            {statusOptions.map((row) => (
                                <option key={row} value={row}>
                                    {row}
                                </option>
                            ))}
                        </select>
                        {formik.touched.status && formik.errors.status && (
                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.status}</div>
                        )}
                    </div>

                    {/* Stock Input */}
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                            Stock (Available Quantity)
                        </label>
                        <input
                            type="text"
                            name="qtyAvailable"
                            placeholder="e.g. 5000"
                            onChange={formik.handleChange}
                            onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                            onFocus={() => setFocusedField('qtyAvailable')}
                            value={formik.values.qtyAvailable}
                            style={getInputStyle('qtyAvailable')}
                        />
                        {formik.touched.qtyAvailable && formik.errors.qtyAvailable && (
                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.qtyAvailable}</div>
                        )}
                    </div>

                    {/* Break quantity and optional unit price */}
                    <div style={{ display: 'flex', gap: '14px', width: '100%', marginBottom: '26px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                                Break Qty
                            </label>
                            <input
                                type="text"
                                name="breakQty"
                                placeholder="e.g. 1000"
                                onChange={formik.handleChange}
                                onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                                onFocus={() => setFocusedField('breakQty')}
                                value={formik.values.breakQty}
                                style={getInputStyle('breakQty')}
                            />
                            {formik.touched.breakQty && formik.errors.breakQty && (
                                <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.breakQty}</div>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                                Unit Price <span style={{ color: '#667085', fontWeight: '400' }}>(optional)</span>
                            </label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    position: 'absolute',
                                    left: '12px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#667085',
                                    fontFamily: `'Public Sans', sans-serif`
                                }}>
                                    ₹
                                </span>
                                <input
                                    type="text"
                                    name="unitPrice"
                                    placeholder="0.00"
                                    onChange={formik.handleChange}
                                    onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                                    onFocus={() => setFocusedField('unitPrice')}
                                    value={formik.values.unitPrice}
                                    style={{
                                        ...getInputStyle('unitPrice'),
                                        paddingLeft: '28px'
                                    }}
                                />
                            </div>
                            {formik.touched.unitPrice && formik.errors.unitPrice && (
                                <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.unitPrice}</div>
                            )}
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #F2F4F7' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                height: '42px',
                                padding: '0 20px',
                                borderRadius: '8px',
                                border: '1px solid #D0D5DD',
                                background: '#FFFFFF',
                                color: '#344054',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: `'Public Sans', sans-serif`,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading}
                            style={{
                                height: '42px',
                                padding: '0 26px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#512DA8',
                                color: '#FFFFFF',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: `'Public Sans', sans-serif`,
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(81, 45, 168, 0.3)',
                                opacity: loading ? 0.7 : 1,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {loading ? "Updating..." : "Update"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateInventoryModal;
